import Actions from '@mm/state/actions/actions';
import companyCRUDActions from '@mm/state/actions/CompanyCRUDActions';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import promotionsCRUDActions from '@mm/state/actions/PromotionsCRUDActions';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import { useInitFacebookSDK } from '@mm/state/hooks/use-init-facebook-sdk';
import { useSetupEnvFetch } from '@mm/state/hooks/use-setup-env-fetch';
import modalActions from '@mm/state/modules/modalsState/actions';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { RouterComponentProps } from './common/helpers/routes/interfaces';
import { useRouteGuard } from './common/hooks/use-route-guard';
import {
  useAddPromoToUser,
  useFetchCompaniesOnSession,
  useGetPlans,
  useGoogleAnalyticsTrackPages,
  useInitialModal,
  useInitNPSPlugin,
  useScrollTopRouteChange,
  useSetUserContextForPlugins,
  useVersionWatcher,
  useFetchGuides,
} from './global-hooks';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

export type GlobalHooksContainerProps = OuterProps & InnerProps;

// Global component to make use of hooks in Main.tsx since it is a class.
// At some point we will convert that to non class based.
const GlobalHooksContainer = (props: GlobalHooksContainerProps) => {
  useAddPromoToUser(props);
  useSetUserContextForPlugins(props);
  useSetupEnvFetch();
  useInitFacebookSDK();
  useFetchCompaniesOnSession(props);
  useGoogleAnalyticsTrackPages(props);
  useScrollTopRouteChange(props);
  useGetPlans(props);
  useInitNPSPlugin(props);
  useVersionWatcher(props);
  useInitialModal(props);
  useRouteGuard();
  useFetchGuides();

  return null;
};

const mapStateToProps = (state: StoreState) => ({
  session: state.session.session,
  companyId: selectActiveCompanyId(state),
  fbAppId: state.env.fbAppId,
  gaTrackingId: state.env.gaTrackingId,
});

const initModalsMap: { [key: string]: keyof typeof modalActions } = {
  'connect-follow-up': 'openConnectPagesFollowUpModal',
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openInitialModal: (modalType: string) => {
    const modalAction = initModalsMap[modalType];
    if (modalAction) {
      return dispatch((modalActions as any)[modalAction]?.());
    }
  },
  addPromoToUser: (companyId: number, promoCode: string) =>
    dispatch(promotionsCRUDActions.addPromoToUser(companyId, promoCode)),
  initFacebookSDK: (fbAppId: number) =>
    dispatch(facebookSDKActions.init(fbAppId)),
  getCompanies: () => dispatch(companyCRUDActions.retrieveAll()),
  getPlans: (companyId: number) =>
    dispatch(subscriptionCRUDActions.getPlans(companyId)),
  notifyMandatoryUpdate: (secondsToReload: number) =>
    dispatch({ type: Actions.NOTIFY_MANDATORY_UPDATE, secondsToReload }),
  notifyRecommendedUpdate: () =>
    dispatch({ type: Actions.NOTIFY_RECOMMENDED_UPDATE }),
});

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GlobalHooksContainer);
